import {UnsafeHTMLDiv} from '@github-ui/safe-html/UnsafeHTML'
import {Stack, ToggleSwitch, Heading, Button, Link, SkeletonBox} from '@primer/react'
import type {FeaturePreviewObject} from '../useFeaturePreview'
import {forwardRef, useId, useImperativeHandle} from 'react'
import styles from './FeaturePreviewCard.module.css'
import {SkeletonText} from '@primer/react/experimental'
import {createEvent} from '../utils/analytics'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {FeatureBanner} from './FeatureBanner'

export interface FeaturePreviewCardProps {
  feature: FeaturePreviewObject
  showBanner?: 'error' | 'reload'
  onUpdate?: (enrolled: boolean) => void
}

export interface FeaturePreviewCardHandle {
  focusHeading: () => void
}

export const FeaturePreviewCard = forwardRef<FeaturePreviewCardHandle, FeaturePreviewCardProps>((props, ref) => {
  const {feature, onUpdate} = props
  const headingId = `feature-preview-dialog-heading-${feature.slug}`
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const toggleLabel = `${feature.enrolled ? 'Disable' : 'Enable'} the ${feature.name} feature preview`

  useImperativeHandle(
    ref,
    () => {
      return {
        focusHeading: () => {
          const headingElement = document.getElementById(headingId)
          if (headingElement) {
            headingElement.focus()
          }
        },
      }
    },
    [headingId],
  )

  return (
    <Stack direction="vertical" className="width-full" padding="normal">
      <Stack direction="horizontal" justify="space-between" className="width-full">
        <Heading as="h1" variant="small" id={headingId} tabIndex={-1}>
          {feature.name}
        </Heading>
        <ToggleSwitch
          aria-labelledby={headingId}
          aria-label={toggleLabel}
          checked={feature.enrolled}
          size="small"
          className="flex-self-baseline"
          onClick={() => {
            sendClickAnalyticsEvent(
              createEvent({
                action: `${feature.enrolled ? 'disable' : 'enable'}_feature_preview:${feature.slug}`,
                label: toggleLabel,
              }),
            )

            onUpdate?.(!feature.enrolled)
          }}
        />
      </Stack>
      {props.showBanner && <FeatureBanner type={props.showBanner} />}
      <ImageCard
        imageSrc={feature.image_link ?? undefined}
        altText={feature.name}
        feedbackLink={feature.feedback_link ?? undefined}
      />

      {feature.description_html && <UnsafeHTMLDiv className="markdown-body" html={feature.description_html} />}

      {feature.documentation_link && <Link href={feature.documentation_link}>Documentation</Link>}
    </Stack>
  )
})

FeaturePreviewCard.displayName = 'FeaturePreviewCard'

export function ImageCard(props: {imageSrc?: string; altText?: string; feedbackLink?: string}) {
  const feedbackDescId = useId()

  return (
    <Stack direction="vertical" gap="none" className={styles.imageCard}>
      {props.imageSrc && props.altText && <img className={styles.img} src={props.imageSrc} alt={props.altText} />}
      {props.feedbackLink && (
        <Stack direction="horizontal" justify="space-between" align="center" className="width-full p-3">
          <span id={feedbackDescId}>Help us improve this and make your experience even better.</span>
          <Button as="a" aria-describedby={feedbackDescId} href={props.feedbackLink} variant="default" size="small">
            Give feedback
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

export function FeaturePreviewLoadingCard() {
  return (
    <Stack direction="vertical" className="width-full" padding="normal">
      <SkeletonText />

      <Stack direction="vertical" gap="none" className={styles.imageCard}>
        <div className={styles.img}>
          <SkeletonBox width="100%" height="180px" />
        </div>
        <Stack direction="horizontal" justify="space-between" align="center" className="width-full p-3">
          <SkeletonText />
        </Stack>
      </Stack>

      <SkeletonText lines={5} />
    </Stack>
  )
}

try{ ImageCard.displayName ||= 'ImageCard' } catch {}
try{ FeaturePreviewLoadingCard.displayName ||= 'FeaturePreviewLoadingCard' } catch {}